"use client";

import { motion } from "framer-motion";
import Link from "next/link";
import { useLang } from "@/contexts/language-context";
import { Ticket, Users, ArrowUpRight } from "lucide-react";

const PAIRS = [
  {
    slug: "event-ticketing",
    icon: Ticket,
    label: { en: "Event Ticketing", es: "Event Ticketing" },
    desc: {
      en: "The site IS the checkout. Branded ticket purchase right from your venue page.",
      es: "El sitio ES el checkout. Compra de tickets branded desde la página del venue.",
    },
  },
  {
    slug: "promoter-portal",
    icon: Users,
    label: { en: "Promoter Links & Commissions", es: "Links y Comisiones de Promotores" },
    desc: {
      en: "Each promoter's tracked link points to your event's ticket checkout. Every sale is attributed to them.",
      es: "El link rastreado de cada promotor apunta al checkout de tu evento. Cada venta queda a su nombre.",
    },
  },
];

export default function MicrositePairs() {
  const { t } = useLang();

  return (
    <section className="relative py-24 px-6 bg-[#0A0814] text-white overflow-hidden">
      <div className="relative max-w-[1080px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="mb-10"
        >
          <div className="text-[11px] font-black tracking-widest uppercase text-white/45 mb-3">
            {t("Pairs well with", "Combina bien con")}
          </div>
          <h2 className="text-2xl md:text-[36px] font-black tracking-tight">
            {t("Site + ticket + promoter. ", "Sitio + ticket + promotor. ")}
            <span className="text-white/40">{t("One funnel.", "Un solo funnel.")}</span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {PAIRS.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.div
                key={p.slug}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.6, delay: 0.1 * i }}
              >
                <Link
                  href={`/features/${p.slug}`}
                  className="group block bg-white/[0.04] backdrop-blur-xl border border-white/10 rounded-[24px] p-7 hover:bg-white/[0.06] hover:border-white/20 hover:-translate-y-1 transition-all"
                >
                  <div className="flex items-start justify-between mb-5">
                    <div
                      className="w-12 h-12 rounded-[14px] flex items-center justify-center group-hover:scale-110 transition-transform"
                      style={{ backgroundColor: "rgba(124, 58, 237, 0.20)" }}
                    >
                      <Icon className="w-5 h-5" style={{ color: "#C4B5FD" }} />
                    </div>
                    <ArrowUpRight className="w-5 h-5 text-white/30 group-hover:text-white group-hover:rotate-12 transition-all" />
                  </div>
                  <div className="text-[18px] font-black text-white mb-2">
                    {t(p.label.en, p.label.es)}
                  </div>
                  <div className="text-[14px] font-medium text-white/55 leading-relaxed">
                    {t(p.desc.en, p.desc.es)}
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
